import * as ReactDOM from "react-dom";
import { CompositeFilterDescriptor, DataResult, State } from "@progress/kendo-data-query";
import { logError } from "../LogHelper";
import { useRef, useState, useEffect, ReactNode } from "react";
import SimpleValidationMessageView from "../validation/SimpleValidationMessageView";
import { getSessionCorrelation } from "../../api/clientFactory";
import * as Helper from "./helper";

interface IProps {
    url: string;
    dataState: State;
    additionalFilters?: CompositeFilterDescriptor | undefined; // for additional filter expressions, which are injected, that the user cannot control
    customFilterExpressions?: Array<ICustomFilterExpression> | undefined; // for custom expressions filter expressions
    additionalQueries?: string[] | undefined; // for additional odata query to add to the string (i.e. $expand). not great for $filter as the additional expressions may clash.
    onDataReceived: (data: DataResult) => void;
    mapDataRow: (dataRow: any) => any;
    showContentAsBusyElementId: string;
    changeToken?: any;
    authTokenProvider?: (() => Promise<string>) | undefined;
}

export interface ICustomFilterExpression {
    name: string;
    expression: (value: any, operator: any) => string | undefined;
}

export default function ODataReader(props: IProps): JSX.Element {
    return (
        <ODataValidator dataState={props.dataState} onDataReceived={props.onDataReceived}>
            <ODataLoaderInternal
                url={props.url}
                authTokenProvider={props.authTokenProvider}
                additionalQueries={props.additionalQueries}
                showContentAsBusyElementId={props.showContentAsBusyElementId}
                dataState={props.dataState}
                onDataReceived={props.onDataReceived}
                mapDataRow={props.mapDataRow}
                changeToken={props.changeToken}
                customFilterExpressions={props.customFilterExpressions}
                additionalFilters={props.additionalFilters}
            />
        </ODataValidator>
    );
}

function ODataValidator(props: { dataState: State; onDataReceived: (data: DataResult) => void; children?: ReactNode }): JSX.Element {
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        setErrorMessage(Helper.getDataStateFilterValidationMessages(props.dataState.filter));
    }, [props.dataState, props.dataState.filter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (errorMessage) props.onDataReceived({ data: [], total: 0 });
    }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {errorMessage && (
                <div className="mb-2">
                    <SimpleValidationMessageView message={errorMessage!} />
                </div>
            )}
            {!errorMessage && props.children}
        </>
    );
}

function ODataLoaderInternal(props: IProps): JSX.Element {
    // the state supplies the filters, paging and sorting
    // provides de-bouncing of events, i.e.
    // - if a request is running another request will not be made until the the running request has completed
    // - when the request is completed it will check to verify if another request is needed

    const requestUrl = Helper.getUrl(props.url, props.dataState, props.additionalQueries, props.customFilterExpressions, props.additionalFilters, false);
    const lastSuccessfulQuery = useRef<string>("");
    const runningQuery = useRef<string>("");
    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    });

    function fetchRequest(requestInit?: RequestInit): void {
        fetch(requestUrl, requestInit)
            .then((response) => {
                if (response.ok) return response.json();
                throw new Error();
            })
            .then((json) => {
                // mark as successful and clear the running query
                lastSuccessfulQuery.current = runningQuery.current;
                runningQuery.current = "";

                // if we've since unmounted then abandon the operation
                if (!isMounted.current) return;

                // request a new query if it doesn't match what the user has now asked for
                const requestedQuery = getChangeTracker(
                    Helper.getUrl(props.url, props.dataState, props.additionalQueries, props.customFilterExpressions, props.additionalFilters, false),
                    props.changeToken
                );
                if (requestedQuery !== lastSuccessfulQuery.current) {
                    requestDataIfNeeded();
                    return;
                }

                // apply the data
                const data: any[] = json.value;
                const total = json["@odata.count"];
                log(`Completed: ${lastSuccessfulQuery.current} (${data.length} rows for ${total} total)`);
                props.onDataReceived({
                    data: data.map((dr: any) => props.mapDataRow(dr)),
                    total: total,
                });
            })
            .catch((e) => {
                // on failure, stop all queries until a change is requested
                // the user will be presented with an empty grid
                // we can expand on this later if needed
                lastSuccessfulQuery.current = runningQuery.current;
                runningQuery.current = "";
                props.onDataReceived({ data: [], total: 0 });
                logError(e); // <-- this will ensure we see the events in application insights
            });
    }

    function requestDataIfNeeded(): void {
        // skip the query if one is running or it's the same as one that has completed
        if (runningQuery.current || getChangeTracker(requestUrl, props.changeToken) === lastSuccessfulQuery.current) {
            return;
        }

        // initiate a fetch operation to load the data
        runningQuery.current = getChangeTracker(requestUrl, props.changeToken);
        log(`Querying: ${runningQuery.current}`);

        // get the access token
        // - do not cache, the provider is expected to manage its own cache (the msal auth service includes one with expiry logic)

        if (props.authTokenProvider) {
            props.authTokenProvider!().then((accessToken) => {
                fetchRequest({
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Session-Correlation": getSessionCorrelation(),
                        Authorization: "Bearer " + accessToken,
                    },
                });
            });
        } else {
            fetchRequest({
                method: "GET",
                headers: {
                    "Session-Correlation": getSessionCorrelation(),
                },
            });
        }
    }

    // initiate load
    requestDataIfNeeded();

    // apply a localised loading panel whilst a query is running
    return runningQuery.current ? <LoadingPanel elementId={props.showContentAsBusyElementId} /> : <></>;

    function LoadingPanel(props: { elementId: string }): JSX.Element {
        const loadingPanel = (
            <div className="k-loading-mask">
                <span className="k-loading-text">Loading</span>
                <div className="k-loading-image" />
                <div className="k-loading-color" />
            </div>
        );

        let element: HTMLElement | null | undefined = document.getElementById(props.elementId);

        // when attaching to a grid, attempt to find and use the content element as this appears to render faster
        element = element?.querySelector(".k-grid-content") ?? element;

        return element ? ReactDOM.createPortal(loadingPanel, element) : loadingPanel;
    }

    function getChangeTracker(requestUrl: string, changeToken: any | undefined) {
        return requestUrl + `(${JSON.stringify(changeToken) ?? ""})`;
    }
}

const showDebugMessages: boolean = false;

function log(message: string) {
    if (!showDebugMessages) return;
    console.debug("ODataReader: " + message);
}
